import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import BedOutlinedIcon from '@mui/icons-material/BedOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Modal from '@mui/material/Modal';
import Popover from '@mui/material/Popover';
import axios from 'axios';
import React, { useEffect, useId, useState } from 'react';
import { circleApi } from 'src/api/circles';
import { getOthRegistrationSchema } from 'src/api/othRegistration';

import styles from './CustomerCardDetails.module.css';
import {
  buildGmailComposeUrl,
  buildMailtoUrl,
  buildOutlookComposeUrl,
  copyEmailToClipboard,
} from './emailCompose';
import {
  customerCardLoadErrorMessage,
  fieldLabelsFromSchema,
  formatAllergies,
  formatComment,
  formatContactShareShort,
  formatContactShareStatus,
  formatHeaderMeta,
  formatTeamName,
  formatUnknownYesNo,
  formatYesNo,
  hasApprovedContactShare,
  mapLeaderMemberDetailsToPerson,
  mapMemberContactToPerson,
} from './mapCustomerCard';
import { CustomerCardPerson, CustomerCardViewerRole } from './types';

const AVATAR_PALETTE = [
  { background: '#d8f3e3', color: '#2f7a54' },
  { background: '#f7d7e4', color: '#9a4b66' },
  { background: '#e4d9fa', color: '#5b4db8' },
  { background: '#f7e7c6', color: '#8a6a2b' },
  { background: '#d6e7f8', color: '#3d6d99' },
];

type LeaderSectionId =
  'contact' | 'registration' | 'invoice' | 'consents' | 'answers' | 'team';

const getInitials = (name: string) => {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const colorFromKey = (key: string) => {
  const hash = Array.from(key).reduce(
    (sum, character) => sum + character.charCodeAt(0),
    0
  );
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
};

type CustomerCardDetailsProps = {
  open: boolean;
  tribeId?: string;
  preview: CustomerCardPerson | null;
  viewerRole: CustomerCardViewerRole;
  currentUserId?: string;
  onClose: () => void;
};

const Avatar: React.FC<{ person: CustomerCardPerson }> = ({ person }) => {
  const palette = colorFromKey(person.id || person.name);

  if (person.avatarUrl) {
    return <img src={person.avatarUrl} alt="" className={styles.avatar} />;
  }

  return (
    <span
      className={styles.initials}
      style={{ background: palette.background, color: palette.color }}
    >
      {getInitials(person.name)}
    </span>
  );
};

const ActionButton: React.FC<{
  href?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  label: string;
  icon: React.ReactNode;
  variant?: 'default' | 'primary';
  ariaExpanded?: boolean;
  ariaHasPopup?: 'menu';
  ariaControls?: string;
}> = ({
  href,
  onClick,
  label,
  icon,
  variant = 'default',
  ariaExpanded,
  ariaHasPopup,
  ariaControls,
}) => {
  const className = `${styles.action} ${
    variant === 'primary' ? styles.actionPrimary : ''
  }`;

  if (onClick) {
    return (
      <button
        type="button"
        className={className}
        onClick={onClick}
        aria-expanded={ariaExpanded}
        aria-haspopup={ariaHasPopup}
        aria-controls={ariaControls}
      >
        {icon}
        {label}
      </button>
    );
  }

  if (!href) {
    return (
      <button type="button" className={className} disabled>
        {icon}
        {label}
      </button>
    );
  }

  return (
    <a className={className} href={href}>
      {icon}
      {label}
    </a>
  );
};

const EmailActionButton: React.FC<{
  email?: string;
  variant?: 'default' | 'primary';
}> = ({ email, variant = 'primary' }) => {
  const menuId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [copied, setCopied] = useState(false);
  const open = Boolean(anchorEl);

  const closeMenu = () => setAnchorEl(null);

  useEffect(() => {
    setCopied(false);
    setAnchorEl(null);
  }, [email]);

  const copyEmail = async () => {
    if (!email) {
      return false;
    }
    try {
      await copyEmailToClipboard(email);
      setCopied(true);
      return true;
    } catch {
      setCopied(false);
      return false;
    }
  };

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    void copyEmail();
  };

  if (!email) {
    return (
      <ActionButton
        label="Send e-post"
        icon={<EmailOutlinedIcon />}
        variant={variant}
      />
    );
  }

  return (
    <>
      <ActionButton
        label="Send e-post"
        icon={<EmailOutlinedIcon />}
        variant={variant}
        onClick={handleOpen}
        ariaExpanded={open}
        ariaHasPopup="menu"
        ariaControls={menuId}
      />
      <Popover
        id={menuId}
        open={open}
        anchorEl={anchorEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        marginThreshold={12}
        sx={{ zIndex: 1500 }}
        PaperProps={{
          className: styles.emailMenu,
          elevation: 0,
          sx: {
            borderRadius: '16px',
            boxShadow: '0 12px 32px rgba(28, 27, 51, 0.16)',
          },
        }}
      >
        <div className={styles.emailMenuList} role="menu">
          <p className={styles.emailMenuAddress}>{email}</p>
          <button
            type="button"
            role="menuitem"
            className={`${styles.emailMenuItem} ${
              copied ? styles.emailMenuItemCopied : ''
            }`}
            aria-live="polite"
            onClick={() => {
              void copyEmail();
            }}
          >
            {copied ? <CheckOutlinedIcon /> : <ContentCopyOutlinedIcon />}
            {copied ? 'E-post kopiert' : 'Kopier e-post'}
          </button>
          <a
            role="menuitem"
            className={styles.emailMenuItem}
            href={buildGmailComposeUrl(email)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeMenu}
          >
            <OpenInNewOutlinedIcon />
            Åpne i Gmail
          </a>
          <a
            role="menuitem"
            className={styles.emailMenuItem}
            href={buildOutlookComposeUrl(email)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeMenu}
          >
            <OpenInNewOutlinedIcon />
            Åpne i Outlook
          </a>
          <a
            role="menuitem"
            className={styles.emailMenuItem}
            href={buildMailtoUrl(email)}
            onClick={closeMenu}
          >
            <EmailOutlinedIcon />
            Åpne i e-postapp
          </a>
        </div>
      </Popover>
    </>
  );
};

const ContactActions: React.FC<{
  phone?: string;
  email?: string;
}> = ({ phone, email }) => (
  <div className={styles.actions}>
    <ActionButton
      href={phone ? `tel:${phone}` : undefined}
      label="Ring"
      icon={<PhoneOutlinedIcon />}
    />
    <EmailActionButton email={email} />
  </div>
);

const DetailRow: React.FC<{
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
  tone?: 'positive' | 'negative' | 'muted' | 'warning' | 'team' | '';
  pill?: boolean;
}> = ({ icon, label, value, tone, pill }) => {
  const isEmpty = value === '—' && !tone;
  const valueClass = `${styles.adminValue} ${tone ? styles[tone] : ''} ${
    isEmpty ? styles.muted : ''
  }`;
  const pillClass =
    tone === 'warning'
      ? styles.pillWarning
      : tone === 'positive'
        ? styles.pillPositive
        : tone === 'negative'
          ? styles.pillNegative
          : '';

  return (
    <div className={styles.adminRow}>
      {icon && (
        <span className={styles.adminIcon} aria-hidden="true">
          {icon}
        </span>
      )}
      <span className={styles.adminLabel}>{label}</span>
      <span className={valueClass}>
        {pill && pillClass ? (
          <span className={`${styles.pill} ${pillClass}`}>{value}</span>
        ) : (
          value
        )}
      </span>
    </div>
  );
};

const CollapsibleSection: React.FC<{
  title: string;
  icon: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ title, icon, open, onToggle, children }) => (
  <section className={styles.section}>
    <h3 className={styles.sectionHeading}>
      <button
        type="button"
        className={styles.sectionToggle}
        aria-expanded={open}
        onClick={onToggle}
      >
        <span className={styles.sectionIcon} aria-hidden="true">
          {icon}
        </span>
        <span className={styles.sectionTitle}>{title}</span>
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </button>
    </h3>
    {open && <div className={styles.sectionBody}>{children}</div>}
  </section>
);

const ConsentNote: React.FC = () => (
  <p className={styles.consentNote}>
    <InfoOutlinedIcon />
    Samtykke skjuler ikke informasjon for ledere og medledere.
  </p>
);

const isCanceled = (error: unknown) =>
  axios.isAxiosError(error) && error.code === 'ERR_CANCELED';

const displayValue = (value?: string): string => value?.trim() || '—';

export const CustomerCardDetails: React.FC<CustomerCardDetailsProps> = ({
  open,
  tribeId,
  preview,
  viewerRole,
  currentUserId,
  onClose,
}) => {
  const [details, setDetails] = useState<CustomerCardPerson | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openSections, setOpenSections] = useState<
    Record<LeaderSectionId, boolean>
  >({
    contact: true,
    registration: true,
    invoice: true,
    consents: true,
    answers: true,
    team: true,
  });

  const userId = preview?.id;
  const teamName = preview?.teamName;

  useEffect(() => {
    if (!open) {
      setDetails(null);
      setError(null);
      setIsLoading(false);
      setOpenSections({
        contact: true,
        registration: true,
        invoice: true,
        consents: true,
        answers: true,
        team: true,
      });
      return;
    }
    if (!tribeId || !userId) {
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    const loadPerson = async () => {
      if (viewerRole === 'leader') {
        const [result, schema] = await Promise.all([
          circleApi.getCircleMemberDetails(tribeId, userId, controller.signal),
          getOthRegistrationSchema(tribeId).catch(() => undefined),
        ]);
        return mapLeaderMemberDetailsToPerson(result, {
          teamName,
          fieldLabels: fieldLabelsFromSchema(schema),
        });
      }

      const result = await circleApi.getCircleContact(
        tribeId,
        userId,
        controller.signal
      );
      return mapMemberContactToPerson(result.user, { teamName });
    };

    loadPerson()
      .then((personDetails) => {
        setDetails(personDetails);
      })
      .catch((loadError: unknown) => {
        if (isCanceled(loadError)) {
          return;
        }
        const status = axios.isAxiosError(loadError)
          ? loadError.response?.status
          : undefined;
        setError(customerCardLoadErrorMessage(status));
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => controller.abort();
  }, [open, tribeId, userId, viewerRole, teamName]);

  const person = details ?? preview;
  if (!open || !person) {
    return null;
  }

  const isLeaderView = viewerRole === 'leader';
  const isOwnCard = Boolean(currentUserId && person.id === currentUserId);
  const canShowContact =
    isLeaderView ||
    isOwnCard ||
    hasApprovedContactShare(person.contactShareConsent);
  const contactShare = formatContactShareStatus(person.contactShareConsent);
  const contactShareShort = formatContactShareShort(
    person.consents?.contactShare ?? person.contactShareConsent
  );
  const ehf = formatYesNo(person.ehfInvoice);
  const hotel = formatYesNo(person.hotelNeeded);
  const photos = formatUnknownYesNo(person.consents?.photos);
  const marketing = formatUnknownYesNo(person.consents?.marketing);
  const teamNameValue = formatTeamName(person.teamName);
  const showMemberTeam = !isLeaderView && Boolean(person.teamName);
  const invoice = person.invoice;
  const customAnswers = person.customAnswers ?? [];
  const headerMeta = formatHeaderMeta(person.company, person.jobTitle);
  const showLeaderBody = isLeaderView && details != null;
  const hasRegistration = details?.hasRegistration === true;
  const toggleSection = (id: LeaderSectionId) => {
    setOpenSections((current) => ({ ...current, [id]: !current[id] }));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="customer-card-name"
      aria-describedby="customer-card-details"
    >
      <Box
        className={`${styles.card} ${isLeaderView ? styles.leaderCard : ''}`}
        tabIndex={-1}
      >
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Lukk kundekort"
        >
          <CloseOutlinedIcon />
        </button>

        <header className={styles.header}>
          <Avatar person={person} />
          <div className={styles.identity}>
            <h2 id="customer-card-name" className={styles.name}>
              {person.name}
            </h2>
            {headerMeta ? (
              <span className={styles.company}>{headerMeta}</span>
            ) : (
              person.company && (
                <span className={styles.company}>{person.company}</span>
              )
            )}
            {isLeaderView && <span className={styles.roleBadge}>Deltaker</span>}
          </div>
        </header>

        <div
          id="customer-card-details"
          className={isLeaderView ? styles.scrollBody : undefined}
        >
          {!isLeaderView && (
            <>
              <hr className={styles.divider} />
              <div className={styles.contactList}>
                <div className={`${styles.contactRow} ${styles.contactRowBio}`}>
                  <span className={styles.contactIcon} aria-hidden="true">
                    <NotesOutlinedIcon />
                  </span>
                  <span
                    className={`${styles.contactValue} ${
                      person.bio ? '' : styles.muted
                    }`}
                  >
                    {person.bio || '—'}
                  </span>
                </div>
                {canShowContact && (
                  <>
                    <div className={styles.contactRow}>
                      <span className={styles.contactIcon} aria-hidden="true">
                        <EmailOutlinedIcon />
                      </span>
                      <span
                        className={`${styles.contactValue} ${
                          person.email ? '' : styles.muted
                        }`}
                      >
                        {person.email || '—'}
                      </span>
                    </div>
                    <div className={styles.contactRow}>
                      <span className={styles.contactIcon} aria-hidden="true">
                        <PhoneOutlinedIcon />
                      </span>
                      <span
                        className={`${styles.contactValue} ${
                          person.phone ? '' : styles.muted
                        }`}
                      >
                        {person.phone || '—'}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {showMemberTeam && (
            <>
              <hr className={styles.divider} />
              <div className={styles.teamRow}>
                <span className={styles.teamIcon} aria-hidden="true">
                  <GroupsOutlinedIcon />
                </span>
                <span className={styles.teamLabel}>Ditt lag:</span>
                <span className={styles.teamValue}>{person.teamName}</span>
              </div>
            </>
          )}

          {showLeaderBody && (
            <div className={styles.leaderSections}>
              <CollapsibleSection
                title="Kontaktprofil"
                icon={<PersonOutlineIcon />}
                open={openSections.contact}
                onToggle={() => toggleSection('contact')}
              >
                <DetailRow
                  icon={<PersonOutlineIcon />}
                  label="Navn"
                  value={displayValue(person.name)}
                />
                <DetailRow
                  icon={<NotesOutlinedIcon />}
                  label="Bio"
                  value={displayValue(person.bio)}
                />
                <DetailRow
                  icon={<EmailOutlinedIcon />}
                  label="E-post"
                  value={displayValue(person.email)}
                />
                <DetailRow
                  icon={<PhoneOutlinedIcon />}
                  label="Mobil"
                  value={displayValue(person.phone)}
                />
                <DetailRow
                  icon={<BusinessOutlinedIcon />}
                  label="Firma"
                  value={displayValue(person.company)}
                />
                <DetailRow
                  icon={<ShieldOutlinedIcon />}
                  label="Deling av kontaktinfo"
                  value={contactShare.text}
                  tone={contactShare.tone}
                  pill
                />
                <ConsentNote />
              </CollapsibleSection>

              <CollapsibleSection
                title="Registrering"
                icon={<AssignmentOutlinedIcon />}
                open={openSections.registration}
                onToggle={() => toggleSection('registration')}
              >
                {hasRegistration ? (
                  <>
                    <DetailRow
                      icon={<PersonOutlineIcon />}
                      label="Fornavn"
                      value={displayValue(person.firstName)}
                    />
                    <DetailRow
                      icon={<PersonOutlineIcon />}
                      label="Etternavn"
                      value={displayValue(person.surname)}
                    />
                    <DetailRow
                      icon={<WorkOutlineIcon />}
                      label="Stilling"
                      value={displayValue(person.jobTitle)}
                    />
                    <DetailRow
                      icon={<SearchOutlinedIcon />}
                      label="Bransje"
                      value={displayValue(person.industry)}
                    />
                    <DetailRow
                      icon={<LayersOutlinedIcon />}
                      label="Ledernivå"
                      value={displayValue(person.leadershipLevel)}
                    />
                    <DetailRow
                      icon={<PersonOutlineIcon />}
                      label="Kjønn"
                      value={displayValue(person.gender)}
                    />
                    <DetailRow
                      icon={<BedOutlinedIcon />}
                      label="Ønsker hotellrom"
                      value={hotel.text}
                      tone={hotel.tone}
                    />
                    <DetailRow
                      icon={<RestaurantOutlinedIcon />}
                      label="Allergier"
                      value={formatAllergies(person.allergies)}
                      tone={person.allergies ? '' : 'muted'}
                    />
                    <DetailRow
                      icon={<ChatBubbleOutlineIcon />}
                      label="Kommentar"
                      value={formatComment(person.comment)}
                      tone={person.comment ? '' : 'muted'}
                    />
                  </>
                ) : (
                  <p className={styles.registrationMissing}>
                    Ingen OTH-registrering levert.
                  </p>
                )}
              </CollapsibleSection>

              {hasRegistration && (
                <>
                  <CollapsibleSection
                    title="Fakturainfo"
                    icon={<DescriptionOutlinedIcon />}
                    open={openSections.invoice}
                    onToggle={() => toggleSection('invoice')}
                  >
                    <DetailRow
                      icon={<BusinessOutlinedIcon />}
                      label="Firmanavn"
                      value={displayValue(invoice?.firmName)}
                    />
                    <DetailRow
                      icon={<BusinessOutlinedIcon />}
                      label="Org.nr"
                      value={displayValue(invoice?.orgNr)}
                    />
                    <DetailRow
                      icon={<EmailOutlinedIcon />}
                      label="Faktura e-post"
                      value={displayValue(invoice?.email)}
                    />
                    <DetailRow
                      icon={<LocalOfferOutlinedIcon />}
                      label="Referanse"
                      value={displayValue(invoice?.reference)}
                    />
                    <DetailRow
                      icon={<ReceiptLongOutlinedIcon />}
                      label="EHF"
                      value={ehf.text}
                      tone={ehf.tone}
                    />
                  </CollapsibleSection>

                  <CollapsibleSection
                    title="Samtykker"
                    icon={<ShieldOutlinedIcon />}
                    open={openSections.consents}
                    onToggle={() => toggleSection('consents')}
                  >
                    <DetailRow
                      icon={<ShieldOutlinedIcon />}
                      label="Kontaktdeling"
                      value={contactShareShort.text}
                      tone={contactShareShort.tone}
                    />
                    <DetailRow
                      icon={<PhotoCameraOutlinedIcon />}
                      label="Bilder / foto"
                      value={photos.text}
                      tone={photos.tone}
                    />
                    <DetailRow
                      icon={<CampaignOutlinedIcon />}
                      label="Markedsføring"
                      value={marketing.text}
                      tone={marketing.tone}
                    />
                    <ConsentNote />
                  </CollapsibleSection>

                  {customAnswers.length > 0 && (
                    <CollapsibleSection
                      title="Svar fra registreringsskjema"
                      icon={<FormatListBulletedIcon />}
                      open={openSections.answers}
                      onToggle={() => toggleSection('answers')}
                    >
                      {customAnswers.map((answer) => (
                        <DetailRow
                          key={answer.id}
                          label={answer.label}
                          value={answer.value}
                        />
                      ))}
                    </CollapsibleSection>
                  )}
                </>
              )}

              <CollapsibleSection
                title="Lag"
                icon={<GroupsOutlinedIcon />}
                open={openSections.team}
                onToggle={() => toggleSection('team')}
              >
                <DetailRow
                  icon={<GroupsOutlinedIcon />}
                  label="Ditt lag"
                  value={teamNameValue}
                  tone={person.teamName ? 'team' : 'muted'}
                />
              </CollapsibleSection>
            </div>
          )}

          {!isLeaderView && (
            <ContactActions
              phone={canShowContact ? person.phone : undefined}
              email={canShowContact ? person.email : undefined}
            />
          )}

          {isLoading && (
            <div className={styles.loading} aria-live="polite">
              <CircularProgress size={22} sx={{ color: '#7c6cf0' }} />
            </div>
          )}
          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}

          {!isLeaderView && (
            <p className={styles.footer}>
              <InfoOutlinedIcon />
              Vises fordi deltakeren har samtykket til deling.
            </p>
          )}
        </div>

        {isLeaderView && (
          <div className={styles.footerBar}>
            <ContactActions phone={person.phone} email={person.email} />
          </div>
        )}
      </Box>
    </Modal>
  );
};
